
import React from "react";

import { toast } from "react-toastify";

import OrderTable from "./OrderTable";

import DiningSection from "./DiningSection";

import PaginationComponent from "../../../utils/TablePagination";

import LoadingScreen from "../../../utils/LoadingScreen";

import { GlobalContext } from "../../../Context/GlobalContext";

import useBhandarewala from "../../../services/Order/UseBhandarewala";

import useMedia from "../../../services/Whatsapp/useMedia";

const BHANDAREWALA_USER_IDS = ["A1437", "A1443"];

const fileToBase64Only = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || "");

      resolve(result.includes(",") ? result.split(",")[1] : result);
    };

    reader.onerror = () => reject(new Error("Unable to read image"));

    reader.readAsDataURL(file);
  });

const getOfflinePaymentRecord = (result) => {
  const responseData = result?.data || {};

  const raw =
    responseData?.response ??
    responseData?.data ??
    responseData?.result ??
    null;

  if (Array.isArray(raw)) {
    return raw[0] && typeof raw[0] === "object" ? raw[0] : null;
  }

  return raw && typeof raw === "object" && Object.keys(raw).length > 0
    ? raw
    : null;
};

const OfflinePaymentModal = ({ order, onClose, onSuccess }) => {
  const { userID } = React.useContext(GlobalContext);

  const { getOfflinePayment, markPaymentOffline } = useBhandarewala();

  const { uploadImage } = useMedia();

  const fullAmount = Number(
    order?.totalOrderPrice ??
      order?.pay_amount ??
      order?.total_order_price ??
      order?.order_amount ??
      0,
  );

  const safeFullAmount = Number.isFinite(fullAmount) ? fullAmount : 0;

  const [paymentType, setPaymentType] = React.useState("0");

  const [partialAmount, setPartialAmount] = React.useState(
    safeFullAmount > 0 ? safeFullAmount.toFixed(2) : "",
  );

  const [refNo, setRefNo] = React.useState("");

  const [proofFile, setProofFile] = React.useState(null);

  const [existingPayment, setExistingPayment] = React.useState(null);

  const [checkingPayment, setCheckingPayment] = React.useState(false);

  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    const fetchExistingPayment = async () => {
      if (!order?.orderid || !userID) return;

      try {
        setCheckingPayment(true);

        const result = await getOfflinePayment({
          userid: userID,

          orderid: order.orderid,
        });

        if (!active) return;

        setExistingPayment(getOfflinePaymentRecord(result));
      } catch (error) {
        console.error("Existing offline payment check error:", error);
      } finally {
        if (active) {
          setCheckingPayment(false);
        }
      }
    };

    fetchExistingPayment();

    return () => {
      active = false;
    };

    // getOfflinePayment is intentionally not included because the service hook

    // returns a new function reference on every render.
  }, [order?.orderid, userID]);

  const changePaymentType = (nextType) => {
    setPaymentType(nextType);

    if (nextType === "0") {
      setPartialAmount(safeFullAmount > 0 ? safeFullAmount.toFixed(2) : "");
    } else {
      setPartialAmount("");
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (submitting) return;

    const finalRefNo = refNo.trim();

    if (!finalRefNo) {
      toast.error("Please enter reference number");

      return;
    }

    if (!proofFile) {
      toast.error("Please select payment proof image");

      return;
    }

    const enteredAmount =
      paymentType === "0" ? safeFullAmount : Number(partialAmount);

    if (!Number.isFinite(enteredAmount) || enteredAmount <= 0) {
      toast.error("Please enter a valid payment amount");

      return;
    }

    if (
      paymentType === "1" &&
      safeFullAmount > 0 &&
      enteredAmount > safeFullAmount
    ) {
      toast.error("Partial amount cannot be greater than full order amount");

      return;
    }

    try {
      setSubmitting(true);

      // 1) Upload the ORIGINAL selected image bytes to Anlook PHP API.

      // No resize/compression/PNG->WebP/JPG->WebP conversion is done here.

      // Base64 is only the transport encoding required by uploadItemImagev2.php.

      const base64 = await fileToBase64Only(proofFile);

      // Preserve the original file name + extension (png/jpg/jpeg/etc.).

      const imageName =
        String(proofFile?.name || "").trim() ||
        `offline_payment\_${order.orderid}\_${Date.now()}`;

      const uploadResult = await uploadImage(imageName, base64, "IMG");

      const imageUrl = String(uploadResult?.image_url || "").trim();

      if (!imageUrl) {
        toast.error(
          uploadResult?.message ||
            uploadResult?.responseMessage ||
            "Unable to upload payment proof",
        );

        return;
      }

      // 2) Send returned Anlook image URL to markPaymentOffline.

      const result = await markPaymentOffline({
        userid: String(userID),

        orderid: String(order.orderid),

        imgUrl: imageUrl,

        refNo: finalRefNo,

        paymentType,

        partialAmount: enteredAmount.toFixed(2),
      });

      if (!result?.success) {
        return;
      }

      toast.success(
        result?.data?.responseMessage ||
          (paymentType === "0"
            ? "Full offline payment marked successfully"
            : "Partial offline payment marked successfully"),
      );

      await onSuccess?.();

      onClose();
    } catch (error) {
      console.error("Offline payment submit error:", error);

      toast.error(error?.message || "Unable to mark offline payment");
    } finally {
      setSubmitting(false);
    }
  };

  const previousRefNo =
    existingPayment?.refNo ||
    existingPayment?.ref_no ||
    existingPayment?.referenceNo ||
    existingPayment?.reference_no ||
    "";

  const previousAmount =
    existingPayment?.partialAmount ??
    existingPayment?.partial_amount ??
    existingPayment?.amount ??
    "";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/45 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">
              Mark Offline Payment
            </h3>

            <p className="mt-0.5 text-xs text-gray-500">
              Order #{order?.orderid} · Full amount ₹{safeFullAmount.toFixed(2)}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-2 py-1 text-xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          {checkingPayment ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
              Checking previous offline payment...
            </div>
          ) : existingPayment ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-xs font-semibold text-amber-800">
                Previous offline payment found
              </p>

              {(previousRefNo || previousAmount !== "") && (
                <p className="mt-1 text-[11px] text-amber-700">
                  {previousRefNo ? `Ref: ${String(previousRefNo)}` : ""}

                  {previousRefNo && previousAmount !== "" ? " · " : ""}

                  {previousAmount !== ""
                    ? `Amount: ₹${Number(previousAmount || 0).toFixed(2)}`
                    : ""}
                </p>
              )}
            </div>
          ) : null}

          <div>
            <label className="mb-2 block text-xs font-semibold text-gray-700">
              Payment Type
            </label>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => changePaymentType("0")}
                className={`rounded-xl border px-3 py-3 text-left transition ${
                  paymentType === "0"
                    ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <span className="block text-sm font-semibold text-gray-900">
                  Full Payment
                </span>

                <span className="mt-0.5 block text-[11px] text-gray-500">
                  Type 0 · ₹{safeFullAmount.toFixed(2)}
                </span>
              </button>

              <button
                type="button"
                onClick={() => changePaymentType("1")}
                className={`rounded-xl border px-3 py-3 text-left transition ${
                  paymentType === "1"
                    ? "border-amber-500 bg-amber-50 ring-1 ring-amber-200"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <span className="block text-sm font-semibold text-gray-900">
                  Partial Payment
                </span>

                <span className="mt-0.5 block text-[11px] text-gray-500">
                  Type 1 · Enter received amount
                </span>
              </button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold text-gray-700">
                Reference No.
              </span>

              <input
                type="text"
                value={refNo}
                onChange={(event) => setRefNo(event.target.value)}
                placeholder="Enter transaction/reference no."
                className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold text-gray-700">
                {paymentType === "0" ? "Full Amount" : "Partial Amount"}
              </span>

              <input
                type="number"
                min="0"
                step="0.01"
                value={partialAmount}
                onChange={(event) => setPartialAmount(event.target.value)}
                disabled={paymentType === "0"}
                placeholder="Enter amount"
                className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-gray-100 disabled:text-gray-600"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-gray-700">
              Payment Proof
            </span>

            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3">
              <input
                type="file"
                accept="image/*"
                onChange={(event) =>
                  setProofFile(event.target.files?.[0] || null)
                }
                className="block w-full text-xs text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-600 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-emerald-700"
              />
            </div>
          </label>

          <div className="flex justify-end gap-2 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="min-w-[140px] rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Processing..." : "Mark Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CurrentOrdersTable = ({
  filter,

  filteredOrders,

  dispatchedOrderIds = [],

  newOrderIds = [],

  handleRowClickCurrent,

  openRowCurrent,

  orderDetails,

  selectedDeliveryPerson,

  getTimeElapsed,

  handlePrintClick,

  handleDirectClick,

  setActionModalOrder,

  setCancelModalOrder,

  reciptDetails,

  KitchenPrint,

  getDiningStatusLabel,

  handleCancel,

  setCashModalOrder,

  updateOrderStatus,

  getNewOrders,

  getOrderHistory,

  startDate,

  endDate,

  getOrderDetails,

  loadingOrders,

  setTakeawayOrder,

  forceSchedulePink,
}) => {
  const { userID } = React.useContext(GlobalContext);

  const [offlinePaymentOrder, setOfflinePaymentOrder] = React.useState(null);

  const isBhandarewalaUser = BHANDAREWALA_USER_IDS.includes(
    String(userID || "")
      .trim()
      .toUpperCase(),
  );

  const shouldShowOfflinePayment = (order) => {
    if (!isBhandarewalaUser || !order) return false;

    const status = String(order?.status || "")
      .toLowerCase()

      .trim();

    const paymentStatus = String(order?.payment_status || "")
      .toLowerCase()

      .trim();

    const isPartial =
      String(order?.is_partial || "")
        .toUpperCase()

        .trim() === "Y";

    if (["rejected", "refunded", "completed", "delivered"].includes(status)) {
      return false;
    }

    // Hide after a normal full payment. Keep available for partial orders so

    // another offline payment can be recorded if required.

    return paymentStatus !== "paid" || isPartial;
  };

  const refreshAfterOfflinePayment = async () => {
    await Promise.all([
      getNewOrders?.(startDate, endDate),

      getOrderHistory?.(startDate, endDate),
    ]);
  };

  const renderOfflinePaymentButton = (order) => {
    if (!shouldShowOfflinePayment(order)) return null;

    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();

          setOfflinePaymentOrder(order);
        }}
        className="inline-flex h-9 w-[118px] shrink-0 items-center justify-center rounded-md border border-emerald-300 bg-white px-2 text-[11px] font-semibold text-emerald-700 shadow-sm transition hover:border-emerald-500 hover:bg-emerald-50"
      >
        Offline Payment
      </button>
    );
  };

  const renderOrderStatus = (order) => {
    const status = order.status?.toLowerCase()?.trim();

    const type = order.orderType;

    const deliveryStatus = order.delivery_partner_status?.toLowerCase()?.trim();

    const paymentStatus = order.payment_status?.toLowerCase()?.trim();

    const isPartialFlag = order.is_partial?.toUpperCase()?.trim();

    const partialPaidFlag = order.partial_paid?.toUpperCase()?.trim();

    const isPartial = isPartialFlag === "Y";

    const partialAmount = Number(order.partial_amount || 0);

    const formattedPartialAmount = Number.isFinite(partialAmount)
      ? partialAmount.toFixed(2)
      : "0.00";

    const mode = order.payment_mode
      ? order.payment_mode.charAt(0).toUpperCase() + order.payment_mode.slice(1)
      : "";

    // Keep final / operational statuses above payment labels.

    if (status === "rejected" || status === "refunded") {
      return (
        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-600 font-semibold">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
    }

    if (status === "prepared") {
      return (
        <span className="inline-flex items-center justify-center w-[170px] h-8 text-sm rounded-full bg-green-100 text-green-600 font-semibold">
          Prepared
        </span>
      );
    }

    if (type === "home_delivery" && deliveryStatus === "food is ready") {
      return (
        <span className="inline-flex items-center justify-center w-[170px] h-8 rounded-full bg-yellow-200 text-yellow-700 text-sm font-semibold whitespace-nowrap">
          Dispatched
        </span>
      );
    }

    // Partial payment selected but not paid yet.

    if (isPartial && !(partialPaidFlag === "Y" && paymentStatus === "paid")) {
      return (
        <span className="inline-flex items-center justify-center min-w-[170px] h-8 px-3 text-xs rounded-full bg-amber-100 text-amber-700 font-semibold whitespace-nowrap">
          Partial Pending ₹{formattedPartialAmount}
        </span>
      );
    }

    // Partial payment successfully paid.

    if (isPartial && partialPaidFlag === "Y" && paymentStatus === "paid") {
      const partialPercentage = String(order.parial_percentage || "").trim();

      return (
        <span className="inline-flex items-center gap-2 min-w-[190px] h-9 px-2.5 rounded-full bg-white border border-green-200 text-xs font-semibold whitespace-nowrap">
          <span className="text-green-700">Partial Paid</span>

          <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-green-600 text-white font-bold">
            {partialPercentage ? `${partialPercentage}% · ` : ""}₹
            {formattedPartialAmount}
          </span>
        </span>
      );
    }

    // No partial payment selected + payment completed = full payment.

    if (
      isPartialFlag === "N" &&
      partialPaidFlag === "N" &&
      paymentStatus === "paid"
    ) {
      return (
        <span className="inline-flex items-center justify-center min-w-[150px] h-8 px-3 text-xs rounded-full bg-green-100 text-green-600 font-semibold whitespace-nowrap">
          {mode ? `${mode} ` : ""}Full Paid
        </span>
      );
    }

    // Existing received behaviour for unpaid/non-partial orders.

    if (status === "received") {
      return (
        <span className="text-sm px-3 py-2 rounded-full bg-blue-100 text-blue-600 font-semibold">
          {type === "dining"
            ? `Table ${order.table_no} Active`
            : "Waiting for Payment"}
        </span>
      );
    }

    // Existing paid fallback, in case an older API response only updates status.

    if (status === "paid") {
      return (
        <span className="inline-flex items-center justify-center w-[190px] h-8 text-sm rounded-full bg-green-100 text-green-600 font-semibold">
          {mode} Paid & Preparing
        </span>
      );
    }

    return (
      <span className="inline-flex items-center justify-center w-[170px] h-8 text-sm rounded-full bg-red-100 text-red-600 font-semibold">
        Not Paid
      </span>
    );
  };

  const formatScheduleDateTime = (dateTime) => {
    if (!dateTime) return "";

    const value = String(dateTime).trim();

    if (!value) return "";

    const [datePart, timePart] = value.split(" ");

    if (!datePart || !timePart) return value;

    const [year, month, day] = datePart.split("-");

    const [hour, minute] = timePart.split(":");

    let h = Number(hour);

    const ampm = h >= 12 ? "PM" : "AM";

    h = h % 12 || 12;

    return `${day}/${month}/${year} ${h}:${minute} ${ampm}`;
  };

  const renderOrderType = (order) => {
    const type = (order.orderType || order.order_type || "")

      .toLowerCase()

      .trim()

      .replace(/\s+/g, "_");

    const config = {
      home_delivery: {
        label: "Home Delivery",

        className: "bg-green-100 text-green-700 border border-green-200",
      },

      takeaway: {
        label: "Takeaway",

        className: "bg-yellow-100 text-yellow-700 border border-yellow-200",
      },

      dining: {
        label: "Dining",

        className: "bg-purple-100 text-purple-700 border border-purple-200",
      },

      car_order: {
        label: "Car Order",

        className: "bg-blue-100 text-blue-700 border border-blue-200",
      },

      office_order: {
        label: "Office Order",

        className: "bg-orange-100 text-orange-700 border border-orange-200",
      },
    };

    const item = config[type] || {
      label: type ? type.replace(/_/g, " ") : "N/A",

      className: "bg-gray-100 text-gray-700 border border-gray-200",
    };

    return (
      <span
        className={`inline-flex items-center justify-center w-[120px] py-2 text-sm font-semibold rounded-lg capitalize ${item.className}`}
      >
        {item.label}
      </span>
    );
  };

  const getScheduleDateTime = (order) => {
    return (
      order?.scheduled_date ||
      order?.schedule_date ||
      order?.scheduleDate ||
      order?.scheduledDate ||
      order?.schedule_time ||
      order?.scheduled_time ||
      ""
    );
  };

  const parseScheduleDateTime = (scheduledDate) => {
    if (!scheduledDate) return null;

    // API format: 2026-06-06 10:00:00

    const value = String(scheduledDate).trim();

    if (!value) return null;

    const [datePart, timePart] = value.split(" ");

    if (!datePart || !timePart) return null;

    const [year, month, day] = datePart.split("-").map(Number);

    const [hours, minutes, seconds] = timePart.split(":").map(Number);

    if (
      !year ||
      !month ||
      !day ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return null;
    }

    return new Date(year, month - 1, day, hours, minutes, seconds || 0);
  };

  const parseDeliveryTimeToMilliseconds = (deliveryTime) => {
    const value = String(deliveryTime ?? "").trim();

    if (!value) return 0;

    if (value.includes(".")) {
      const [minutePart = "0", secondPart = "0"] = value.split(".");

      const minutes = Number(minutePart) || 0;

      let seconds = Number(secondPart) || 0;

      if (secondPart.length === 1) {
        seconds *= 10;
      }

      return minutes * 60 * 1000 + seconds * 1000;
    }

    const minutes = Number(value);

    if (!Number.isFinite(minutes)) {
      return 0;
    }

    return minutes * 60 * 1000;
  };

  const formatDispatchActiveTime = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",

      minute: "2-digit",

      hour12: true,
    });
  };

  const isScheduleOrder = (order) => {
    const orderType = (order?.orderType || order?.order_type || "")

      .toLowerCase()

      .trim();

    const status = order?.status?.toLowerCase()?.trim();

    return (
      orderType === "schedule" ||
      orderType === "scheduled" ||
      status === "schedule" ||
      status === "scheduled" ||
      Boolean(String(getScheduleDateTime(order) || "").trim())
    );
  };

  const getScheduleDispatchActiveTime = (order) => {
    const scheduledDate = getScheduleDateTime(order);

    if (!scheduledDate) {
      return null;
    }

    const scheduleTime = parseScheduleDateTime(scheduledDate);

    if (!scheduleTime) {
      return null;
    }

    const deliveryTimeMs = parseDeliveryTimeToMilliseconds(
      order?.delivery_time,
    );

    const additionalBufferMs = 10 * 60 * 1000;

    const totalBufferMs = deliveryTimeMs + additionalBufferMs;

    return scheduleTime.getTime() - totalBufferMs;
  };

  const isScheduleDispatchAllowed = (order) => {
    if (!getScheduleDateTime(order)) {
      return true;
    }

    const activeFrom = getScheduleDispatchActiveTime(order);

    if (!activeFrom) {
      return false;
    }

    return Date.now() >= activeFrom;
  };

  const isOrderAlreadyDispatched = (order) => {
    const status = order.status?.toLowerCase()?.trim();

    const deliveryStatus = order.delivery_partner_status?.toLowerCase()?.trim();

    return (
      status === "dispatched" ||
      status === "ready_for_takeaway" ||
      status === "ready_for_dinning" ||
      status === "delivered" ||
      status === "completed" ||
      deliveryStatus === "food is ready" ||
      deliveryStatus === "dispatched"
    );
  };

  const renderActionButton = (order) => {
    const status = order.status?.toLowerCase()?.trim();

    const type = (order.orderType || order.order_type || "")

      .toLowerCase()

      .trim()

      .replace(/\s+/g, "_");

    const paymentStatus = order.payment_status?.toLowerCase()?.trim();

    const isPaid =
      paymentStatus === "paid" ||
      status === "paid" ||
      order.payment_mode?.toLowerCase()?.trim() === "cod";

    const deliveryStatus = order.delivery_partner_status?.toLowerCase()?.trim();

    const isDispatching = dispatchedOrderIds.includes(order.orderid);

    const scheduleOrder = isScheduleOrder(order);

    const scheduleAllowed = isScheduleDispatchAllowed(order);

    const dispatchActiveTimestamp = getScheduleDispatchActiveTime(order);

    const dispatchActiveTime = formatDispatchActiveTime(
      dispatchActiveTimestamp,
    );

    if (scheduleOrder && !scheduleAllowed) {
      return (
        <div className="flex justify-start">
          <div className="flex min-h-10 w-[165px] items-center justify-center rounded-md border border-pink-300 bg-pink-50 px-2.5 py-1.5 text-center">
            <p className="text-[11px] font-medium leading-4 text-pink-600">
              Dispatch at{" "}
              <span className="font-bold">
                {dispatchActiveTime || "Calculating..."}
              </span>
            </p>
          </div>
        </div>
      );
    }

    const alreadyDispatched = isOrderAlreadyDispatched(order);

    const cancelBtn = (
      <button
        onClick={(e) => {
          e.stopPropagation();

          setCancelModalOrder(order);
        }}
        className="inline-flex h-9 w-[80px] shrink-0 items-center justify-center rounded-md bg-red-500 px-2 text-xs font-medium text-white transition hover:bg-red-600"
      >
        Cancel
      </button>
    );

    if (alreadyDispatched) {
      return (
        <span className="inline-flex items-center justify-center w-[160px] h-10 rounded text-sm bg-gray-100 text-gray-500 font-semibold">
          Already Dispatched
        </span>
      );
    }

    // =========================

    // HOME DELIVERY

    // =========================

    if (type === "home_delivery") {
      const disabled = !isPaid || isDispatching;

      return (
        <div className="flex items-center gap-2 whitespace-nowrap">
          <button
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();

              if (disabled) return;

              setActionModalOrder(order);
            }}
            className={`relative inline-flex h-9 w-[145px] shrink-0 items-center justify-center rounded-md px-2 text-xs font-medium ${
              disabled
                ? "cursor-not-allowed border border-gray-300 bg-white text-gray-400"
                : deliveryStatus === "acknowledged"
                  ? "bg-green-600 text-white"
                  : deliveryStatus === "food is ready"
                    ? "bg-yellow-500 text-black"
                    : "border border-green-600 bg-white text-green-600"
            }`}
          >
            {isDispatching ? "Dispatching..." : "Dispatch Order"}

            {deliveryStatus === "acknowledged" && !disabled && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-yellow-300 px-1.5 py-0.5 text-[9px] font-bold text-yellow-800">
                Assigned
              </span>
            )}
          </button>

          {cancelBtn}
        </div>
      );
    }

    // =========================

    // UNPAID / RECEIVED

    // =========================

    if (status === "received" && !isPaid) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();

            setCashModalOrder(order);
          }}
          className="inline-flex h-9 w-[130px] items-center justify-center rounded-md bg-green-500 px-2 text-xs font-medium text-white"
        >
          Collect Cash
        </button>
      );
    }

    // =========================

    // TAKEAWAY / CAR / OFFICE

    // =========================

    if (
      ["paid", "accepted", "prepared", "received"].includes(status) &&
      ["takeaway", "car_order", "office_order"].includes(type)
    ) {
      const disabled = isDispatching;

      return (
        <div className="flex items-center gap-2 whitespace-nowrap">
          <button
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();

              if (disabled) return;

              if (type === "takeaway") {
                setTakeawayOrder(order);

                return;
              }

              handleDirectClick(order);
            }}
            className={`inline-flex h-9 w-[140px] shrink-0 items-center justify-center rounded-md px-2 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-60 ${
              type === "office_order"
                ? "border border-orange-500 bg-orange-50 text-orange-600 hover:bg-orange-100"
                : "border border-blue-500 bg-white text-blue-600 hover:bg-blue-50"
            }`}
          >
            {isDispatching ? "Dispatching..." : "Dispatch Order"}
          </button>

          {isPaid && cancelBtn}
        </div>
      );
    }

    // =========================

    // DINING

    // =========================

    if (
      ["paid", "accepted", "prepared"].includes(status) &&
      type === "dining"
    ) {
      return (
        <div className="flex items-center gap-2 whitespace-nowrap">
          <button
            onClick={(e) => {
              e.stopPropagation();

              updateOrderStatus({
                userid: order.userid,

                orderid: order.orderid,

                productid: "",

                status: "ready_for_dinning",

                time: "",

                payment: "",
              });
            }}
            className="inline-flex h-9 w-[100px] shrink-0 items-center justify-center rounded-md bg-blue-500 px-2 text-xs font-medium text-white"
          >
            Prepare
          </button>
        </div>
      );
    }

    if (status === "rejected" || status === "refunded") {
      return (
        <span className="text-xs text-red-500">{order.remark || "NA"}</span>
      );
    }

    return (
      <button
        disabled
        className="inline-flex h-9 w-[100px] items-center justify-center rounded-md bg-gray-300 px-2 text-xs text-gray-500"
      >
        No Action
      </button>
    );
  };

  const renderPaymentStatus = (order) => {
    const s = order.payment_status?.toLowerCase()?.trim();

    //  Unpaid

    if (s !== "paid") {
      return (
        <span className="text-xs px-2 py-1 rounded text-white bg-gray-700">
          00:00
        </span>
      );
    }

    //  Paid → time calculate

    const timeData = getTimeElapsed(order.order_date, order.payment_status);

    return (
      <span
        className={`text-xs px-2 py-1 rounded text-white ${
          timeData.isLate ? "bg-red-600" : "bg-green-600"
        }`}
      >
        {timeData.time}
      </span>
    );
  };

  const formatOrderType = (order) => {
    return (order.orderType || order.order_type || "")

      .toLowerCase()

      .trim()

      .replace(/\s+/g, "_");
  };

  const getOrderPreparationMinutes = () => {
    try {
      const userData = JSON.parse(sessionStorage.getItem("user") || "{}");

      const minutes = Number(userData?.order_time);

      return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
    } catch {
      return 0;
    }
  };

  const parseOrderDateTime = (orderDate) => {
    if (!orderDate) return null;

    const [datePart, timePart, period] = String(orderDate).split(" ");

    if (!datePart || !timePart) return null;

    const [day, month, year] = datePart.split("/").map(Number);

    let [hours, minutes, seconds] = timePart.split(":").map(Number);

    if (
      !day ||
      !month ||
      !year ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return null;
    }

    if (period?.toUpperCase() === "PM" && hours !== 12) hours += 12;

    if (period?.toUpperCase() === "AM" && hours === 12) hours = 0;

    return new Date(year, month - 1, day, hours, minutes, seconds || 0);
  };

  const [timerTick, setTimerTick] = React.useState(Date.now());

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimerTick(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const isFoodReadyButtonActiveForRow = (order) => {
    const type = formatOrderType(order);

    const paymentStatus = order.payment_status?.toLowerCase()?.trim();

    const deliveryStatus = order.delivery_partner_status?.toLowerCase()?.trim();

    // only home delivery paid orders

    if (type !== "home_delivery") return false;

    if (paymentStatus !== "paid") return false;

    if (deliveryStatus === "food is ready") return false;

    const preparationMinutes = getOrderPreparationMinutes();

    const orderDateTime = parseOrderDateTime(order.order_date);

    if (!preparationMinutes || !orderDateTime) return true;

    const allowedTime =
      orderDateTime.getTime() + preparationMinutes * 60 * 1000;

    return timerTick >= allowedTime;
  };

  const [visibleNewIds, setVisibleNewIds] = React.useState([]);

  React.useEffect(() => {
    if (newOrderIds.length > 0) {
      newOrderIds.forEach((id) => {
        setVisibleNewIds((prev) => (prev.includes(id) ? prev : [...prev, id]));

        setTimeout(() => {
          setVisibleNewIds((prev) => prev.filter((item) => item !== id));
        }, 120000);
      });
    }
  }, [newOrderIds]);

  const [currentPage, setCurrentPage] = React.useState(1);

  const itemsPerPage = 15;

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;

  const endIndex = startIndex + itemsPerPage;

  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  if (filter === "rejected") {
    return (
      <>
        <table className="w-full text-sm montserrat-font-1 ">
          <thead className="bg-green-100 montserrat-font-1 ">
            <tr>
              <th className="p-4 text-start">S.No</th>

              <th className="p-4 text-start">Order ID</th>

              <th className="p-4 text-start">Date</th>

              <th className="p-4 text-start">Time</th>

              <th className="p-4 text-start">Customer</th>

              <th className="p-4 text-start">Type</th>

              <th className="p-4 text-start">Status</th>

              <th className="p-4 text-start">Reason</th>
            </tr>
          </thead>

          <tbody>
            {loadingOrders ? (
              <tr>
                <td colSpan="8" className="text-center py-10">
                  <LoadingScreen text="Loading orders..." />
                </td>
              </tr>
            ) : paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-500">
                  No Data Found
                </td>
              </tr>
            ) : (
              paginatedOrders.map((order, index) => {
                const isNew = visibleNewIds.includes(order.orderid);

                const scheduleRow =
                  forceSchedulePink ||
                  (isScheduleOrder && isScheduleOrder(order));

                return (
                  <React.Fragment key={order.orderid}>
                    <tr
                      className={`border-t cursor-pointer transition ${
                        scheduleRow
                          ? "bg-pink-100 hover:bg-pink-200 border-l-4 border-pink-500"
                          : isNew
                            ? "animate-slideInRight bg-green-50 hover:bg-green-50 border-l-4 border-green-500"
                            : "hover:bg-green-50"
                      }`}
                      onClick={() =>
                        handleRowClickCurrent(index, order.orderid)
                      }
                    >
                      <td className="p-4">{index + 1}</td>

                      <td>
                        {order.orderid}

                        {isNew && (
                          <span className="ml-2 text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded-full font-bold">
                            NEW
                          </span>
                        )}
                      </td>

                      <td>{order.order_date?.split(" ")[0]}</td>

                      <td>{renderPaymentStatus(order)}</td>

                      <td>
                        <div>{order.customer_name || "N/A"}</div>

                        <div className="text-xs text-gray-500">
                          {order.customer_mobile || "N/A"}
                        </div>
                      </td>

                      <td>{renderOrderType(order)}</td>

                      <td>{order.status}</td>

                      <td>{order.remark || "—"}</td>
                    </tr>

                    {openRowCurrent === index && (
                      <tr className="bg-gray-100">
                        <td colSpan="8">
                          <OrderTable
                            orderDetails={orderDetails}
                            selectedDeliveryPerson={selectedDeliveryPerson}
                            activeTab="current"
                            reciptDetails={reciptDetails}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </>
    );
  }

  if (filter === "dining") {
    if (loadingOrders) {
      return <LoadingScreen text="Loading dining orders..." />;
    }

    if (filteredOrders.length === 0) {
      return (
        <div className="text-center py-10 text-gray-500">No Data Found</div>
      );
    }

    return (
      <DiningSection
        orders={filteredOrders}
        onRowClick={handleRowClickCurrent}
        onCancel={handleCancel}
        onCollectCash={setCashModalOrder}
        updateOrderStatus={updateOrderStatus}
        getNewOrders={getNewOrders}
        getOrderHistory={getOrderHistory}
        startDate={startDate}
        endDate={endDate}
        getTimeElapsed={getTimeElapsed}
        getDiningStatusLabel={getDiningStatusLabel}
        getOrderDetails={getOrderDetails}
      />
    );
  }

  return (
    <>
      <div className="relative w-full h-full montserrat-font-1">
        <table className="w-full table-fixed text-sm montserrat-font-1">
          <thead className="sticky top-0 z-10 bg-green-100">
            <tr>
              <th className="w-[90px] p-3 text-start">Daily OR No</th>

              <th className="w-[200px] p-3 text-start">Order ID</th>

              <th className="w-[120px] p-3 text-start">Date</th>

              <th className="w-[80px] p-3 text-start">Time</th>

              <th className="w-[150px] p-3 text-start">Customer</th>

              <th className="w-[130px] p-3 text-start">Type</th>

              <th className="w-[190px] p-3 text-start">Status</th>

              {isBhandarewalaUser && (
                <th className="w-[135px] p-3 text-start">Payment</th>
              )}

              <th className="w-[245px] p-3 text-start">Action</th>

              <th className="w-[100px] p-3 text-start">Print</th>
            </tr>
          </thead>

          <tbody>
            {loadingOrders ? (
              <tr>
                <td
                  colSpan={isBhandarewalaUser ? 10 : 9}
                  className="text-center py-10"
                >
                  <LoadingScreen text="Loading orders..." />
                </td>
              </tr>
            ) : paginatedOrders.length === 0 ? (
              <tr>
                <td
                  colSpan={isBhandarewalaUser ? 10 : 9}
                  className="text-center py-6 text-gray-500"
                >
                  No Data Found
                </td>
              </tr>
            ) : (
              paginatedOrders.map((order, index) => {
                const isNew = visibleNewIds.includes(order.orderid);

                const scheduleRow = forceSchedulePink || isScheduleOrder(order);

                const scheduleAllowed = isScheduleDispatchAllowed(order);

                const isFoodReadyActive =
                  isFoodReadyButtonActiveForRow(order) &&
                  (!scheduleRow || scheduleAllowed);

                return (
                  <React.Fragment key={order.orderid}>
                    <tr
                      className={`border-t cursor-pointer montserrat-font-1 transition ${
                        scheduleRow
                          ? "bg-pink-100 hover:bg-pink-200 border-l-4 border-pink-500"
                          : isFoodReadyActive
                            ? "bg-blue-100 hover:bg-blue-200 border-l-4 border-blue-500"
                            : isNew
                              ? "animate-slideInRight bg-green-50 hover:bg-green-50 border-l-4 border-green-500"
                              : "hover:bg-green-50"
                      }`}
                      onClick={() =>
                        handleRowClickCurrent(index, order.orderid)
                      }
                    >
                      <td className="px-3 py-7 align-middle">
                        <strong>{order.daily_order_id || "N/A"}</strong>
                      </td>

                      <td className="text-start align-middle px-3 py-2 overflow-hidden">
                        <div className="flex items-start gap-3 w-[230px] max-w-[230px]">
                          {/* LEFT SIDE: Order info */}

                          <div className="flex flex-col items-start gap-1 min-w-[95px] max-w-[115px]">
                            <span className="text-[11px] font-bold text-black whitespace-nowrap">
                              ORD: {order.orderid}
                            </span>

                            {getScheduleDateTime(order) && (
                              <span className="text-[12px] text-pink-700 font-semibold whitespace-nowrap leading-none">
                                {formatScheduleDateTime(
                                  getScheduleDateTime(order),
                                )}
                              </span>
                            )}

                            {order.srNo && (
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap">
                                SR: {order.srNo}
                              </span>
                            )}
                          </div>

                          {/* RIGHT SIDE: Badges */}

                          <div className="flex flex-col items-start gap-1 min-w-[85px]">
                            {isFoodReadyActive && (
                              <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-blue-600 text-white text-[9px] font-bold leading-none whitespace-nowrap">
                                FOOD READY ACTIVE
                              </span>
                            )}

                            {scheduleRow && (
                              <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-pink-500 text-white text-[9px] font-bold leading-none whitespace-nowrap">
                                SCHEDULE
                              </span>
                            )}

                            {isNew && (
                              <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-green-500 text-white text-[9px] font-bold leading-none whitespace-nowrap">
                                NEW
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="text-sm">
                        <div>{order.order_date?.split(" ")[0]}</div>

                        <div className="text-xs text-gray-500">
                          {order.order_date?.split(" ").slice(1).join(" ")}{" "}
                        </div>
                      </td>

                      <td>{renderPaymentStatus(order)}</td>

                      <td>
                        <div>{order.customer_name}</div>

                        <div className="text-sm text-gray-900">
                          {order.customer_mobile}
                        </div>
                      </td>

                      <td>{renderOrderType(order)}</td>

                      <td className="align-middle">
                        {renderOrderStatus(order)}
                      </td>

                      {isBhandarewalaUser && (
                        <td className="align-middle">
                          {renderOfflinePaymentButton(order) || (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      )}

                      <td className="pr-3 align-middle">
                        {renderActionButton(order)}
                      </td>

                      <td className="align-middle">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();

                            handlePrintClick(order);
                          }}
                          className="inline-flex items-center justify-center w-[100px] h-10 bg-blue-500 rounded text-white"
                        >
                          Print
                        </button>

                        {/* <button

                      onClick={(e) => {

                        e.stopPropagation();

                        handleDirectClick(order);

                      }}

                      className="px-3 py-2 bg-green-500 rounded text-white"

                    >

                      Direct

                    </button> */}
                      </td>
                    </tr>

                    {openRowCurrent === index && (
                      <tr className="bg-gray-50">
                        <td
                          colSpan={isBhandarewalaUser ? 10 : 9}
                          className="p-0"
                        >
                          <div className="w-full max-w-full overflow-hidden border-t border-gray-200">
                            <div className="w-full max-w-full overflow-x-auto p-3 bg-blue-50">
                              <div className="min-w-full">
                                <OrderTable
                                  orderDetails={orderDetails}
                                  selectedDeliveryPerson={
                                    selectedDeliveryPerson
                                  }
                                  activeTab="current"
                                  handleKitchenPrint={KitchenPrint}
                                  reciptDetails={reciptDetails}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>

        {offlinePaymentOrder && (
          <OfflinePaymentModal
            order={offlinePaymentOrder}
            onClose={() => setOfflinePaymentOrder(null)}
            onSuccess={refreshAfterOfflinePayment}
          />
        )}

        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          onNextPage={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          onPrevPage={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          onPageClick={(page) => setCurrentPage(page)}
        />
      </div>
    </>
  );
};

export default CurrentOrdersTable;